import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Send,
  Help,
  QuestionAnswer,
  Edit,
  SmartToy,
  Person,
  Lightbulb
} from '@mui/icons-material';
import { getAIFeedbackSync } from "../../Utils/aiUtils";
import { postRequest, getRequest } from "../../Fetch/request";
import { AI_INTRODUCTIONS, UI_TEXT, STORAGE_KEYS } from '../../Utils/constants';

// Import schema helper function to get proper schema based on component type
import { getSchemaForComponent } from '../../Utils/aiPrompts';

export default function ClarificationPanel({ 
  problemId, 
  problemTitle, 
  componentType = "LLD",
  onClarificationsChange = () => {},
  initialConversation = null
}) {
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isInterviewStarted, setIsInterviewStarted] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const chatEndRef = useRef(null);


  // Load clarifications from backend metadata
  const loadClarificationsFromBackend = async () => {
    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      if (!userId || !problemId) {
        return [];
      }

      const endpoint = componentType === 'LLD' 
        ? `/lld-attempt?userId=${userId}&lldProblemId=${problemId}`
        : `/hld-attempt?userId=${userId}&hldProblemId=${problemId}`;
      
      const response = await getRequest(`${process.env.REACT_APP_URL}${endpoint}`);
      
      if (response && response.clarifications) {
        return response.clarifications;
      } else {
        return [];
      }
    } catch (error) {
      console.error('❌ Clarification: Error loading from backend:', error);
      
      // Check if it's a JSON parsing error (likely HTML response from 404)
      if (error.message && error.message.includes('Unexpected token')) {
        console.error('❌ Clarification: Backend returned HTML instead of JSON - likely 404 or server error');
      }
      
      return [];
    }
  };

  // Load conversation from props or localStorage/backend
  useEffect(() => {
    if (!problemId) return;
    
    const loadConversation = async () => {
      try {
        let savedClarifications = [];
        
        // If initialConversation is provided (even if empty), use it directly - don't fall back to other sources
        if (initialConversation !== null && initialConversation !== undefined && Array.isArray(initialConversation)) {
          savedClarifications = initialConversation;
        } else {
          // Fallback: Load from localStorage
          const attemptKey = componentType === 'LLD' ? STORAGE_KEYS.LLD_ATTEMPT(problemId) : STORAGE_KEYS.HLD_ATTEMPT(problemId);
          
          const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          
          savedClarifications = attemptData.clarifications || [];
          
          // If no clarifications in localStorage, try to load from backend
          if (savedClarifications.length === 0) {
            savedClarifications = await loadClarificationsFromBackend();
            
            // Save to localStorage for future use
            if (savedClarifications.length > 0) {
              attemptData.clarifications = savedClarifications;
              localStorage.setItem(attemptKey, JSON.stringify(attemptData));
            }
          }
        }
        
        // Convert clarifications to conversation format
        const conversationFromClarifications = [];
        
        // Always start with AI introduction
        const aiIntroContent = getAIIntroduction();
        
        const aiMessage = {
          id: `ai-intro-${Date.now()}`,
          type: 'ai',
          content: aiIntroContent,
          timestamp: new Date().toISOString()
        };
        conversationFromClarifications.push(aiMessage);
        
        // Load existing conversation if any
        if (savedClarifications.length > 0) {
          savedClarifications.forEach((clarification, index) => {
            // Add user message
            const userContent = clarification.userInput || clarification.text;
            
            conversationFromClarifications.push({
              id: `user-${Date.now()}-${index}`,
              type: 'user',
              content: userContent,
              timestamp: new Date().toISOString()
            });
            
            // Add AI response if available
            if (clarification.aiResponse || clarification.aiFeedback) {
              const aiContent = clarification.aiResponse || clarification.aiFeedback;
              let displayContent = '';
              
              // Handle structured JSON responses (like from new API)
              if (typeof aiContent === 'string') {
                // Try to parse JSON string first
                try {
                  const parsedContent = JSON.parse(aiContent);
                  
                  // Extract only the answer field
                  if (parsedContent.answer) {
                    displayContent = parsedContent.answer;
                  } else {
                    // Fallback to stringifying the parsed object
                    displayContent = JSON.stringify(parsedContent, null, 2);
                  }
                } catch (parseError) {
                  // Not JSON, treat as plain text
                  displayContent = aiContent;
                }
              } else if (typeof aiContent === 'object' && aiContent !== null) {
                // Extract only the answer field
                if (aiContent.answer) {
                  displayContent = aiContent.answer;
                } else {
                  // Fallback to stringifying the object
                  displayContent = JSON.stringify(aiContent, null, 2);
                }
              } else {
                // Handle other types
                displayContent = String(aiContent);
              }
              
              conversationFromClarifications.push({
                id: `ai-${Date.now()}-${index}`,
                type: 'ai',
                content: displayContent,
                timestamp: new Date().toISOString()
              });
            }
          });
        }
        
        setIsInterviewStarted(true);
        setConversation(conversationFromClarifications);
    } catch (error) {
    }
    };
    
    loadConversation();
  }, [problemId, componentType, initialConversation]); // React to changes in initialConversation

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Get user's email initial for avatar
  const getUserInitial = () => {
    try {
      const profileObject = JSON.parse(localStorage.getItem('profileObject') || '{}');
      const email = profileObject.email || '';
      return email.charAt(0).toUpperCase() || 'U';
    } catch (error) {
      return 'U';
    }
  };

  // Get problem understanding data from problem details
  const getProblemUnderstandingData = () => {
    try {
      // Get from problem-specific localStorage key: hld-problem-${problemId}
      // This contains the problem template data (questions, expected answers)
      if (!problemId) {
        return "Problem understanding data not available. Please refer to the problem description for requirements.";
      }
      
      const problemKey = componentType === 'HLD' 
        ? `hld-problem-${problemId}` 
        : `lld-problem-${problemId}`;
      
      let problemData = JSON.parse(localStorage.getItem(problemKey) || 'null');
      
      // If not found in problem-specific key, try to get from attempt localStorage
      if (!problemData) {
        const attemptKey = componentType === 'HLD'
          ? `hld-attempt-${problemId}`
          : `lld-attempt-${problemId}`;
        const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        problemData = attemptData.problemDetails || null;
      }
      
      // If still not found, return fallback message
      if (!problemData) {
        return "Problem understanding data is being loaded. Please wait a moment and try again, or refer to the problem description for requirements.";
      }
      
      // Handle different data structures
      // If it's an array (old format), filter for problem understanding phase
      if (Array.isArray(problemData) && problemData.length > 0) {
        const problemUnderstandingQuestions = problemData.filter(item => 
          item.phase === "Problem understanding"
        );
        
        if (problemUnderstandingQuestions.length > 0) {
          let understandingData = "PROBLEM UNDERSTANDING REQUIREMENTS:\n\n";
          
          problemUnderstandingQuestions.forEach((question, index) => {
            understandingData += `${index + 1}. ${question.name}:\n`;
            understandingData += `   Question: ${question.base_question}\n`;
            understandingData += `   Expected Information:\n`;
            question.expected_info.forEach((info, infoIndex) => {
              understandingData += `   - ${info}\n`;
            });
            understandingData += `\n`;
          });
          
          return understandingData;
        }
      }
      
      // If it's an object (new format), extract relevant information
      if (typeof problemData === 'object' && !Array.isArray(problemData)) {
        // For HLD, we have functionalRequirements, scale, deepDiveQuestions
        // Build context from available data
        let understandingData = "PROBLEM UNDERSTANDING REQUIREMENTS:\n\n";
        
        if (problemData.functionalRequirements && problemData.functionalRequirements.length > 0) {
          understandingData += "Functional Requirements:\n";
          problemData.functionalRequirements.forEach((req, index) => {
            understandingData += `${index + 1}. ${req}\n`;
          });
          understandingData += "\n";
        }
        
        return understandingData || "Problem understanding data not available. Please refer to the problem description for requirements.";
      }
      
      // Fallback if no problem data available
      return "Problem understanding data not available. Please refer to the problem description for requirements.";
    } catch (error) {
      console.error('Error loading problem understanding data:', error);
      return "Problem understanding data not available. Please refer to the problem description for requirements.";
    }
  };

  // Get enhanced context for AI prompts
  const getEnhancedContext = () => {
    const attemptKey = componentType === 'LLD' ? STORAGE_KEYS.LLD_ATTEMPT(problemId) : STORAGE_KEYS.HLD_ATTEMPT(problemId);
    const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Helper function to extract correct items
    const getCorrectItems = (list) => {
      if (!list || !Array.isArray(list)) return [];
      
      return list
        .filter(item => {
          if (!item) return false;
          
          // If item has evaluationStatus, check if it's correct
          if (item.evaluationStatus !== undefined) {
            return item.evaluationStatus === 'correct' && 
                   item.userInput && 
                   item.userInput.trim();
          }
          
          // If item has text property (from TextListBuilder format)
          if (item.text !== undefined) {
            return item.text && item.text.trim();
          }
          
          // If item has userInput but no evaluationStatus, assume it's correct
          if (item.userInput !== undefined) {
            return item.userInput && item.userInput.trim();
          }
          
          // If item is a string, assume it's correct
          if (typeof item === 'string') {
            return item.trim();
          }
          
          return false;
        })
        .map(item => {
          // Extract the actual text content
          if (item.userInput) return item.userInput;
          if (item.text) return item.text;
          if (typeof item === 'string') return item;
          return '';
        })
        .filter(Boolean);
    };
    
    // Get user's already added content based on component type
    let userContent = '';
    
    if (componentType === 'LLD') {
      // For LLD: Include features, entities, design patterns
      const features = getCorrectItems(attemptData.features || []);
      const entities = getCorrectItems(attemptData.entities || []);
      const designPatterns = getCorrectItems(attemptData.designPatterns || []);
      
      if (features.length > 0 || entities.length > 0 || designPatterns.length > 0) {
        userContent = `\n\nUser's Already Added Content:\n`;
        
        if (features.length > 0) {
          userContent += `- Features: ${features.join(', ')}\n`;
        }
        
        if (entities.length > 0) {
          userContent += `- Entities: ${entities.join(', ')}\n`;
        }
        
        if (designPatterns.length > 0) {
          userContent += `- Design Patterns: ${designPatterns.join(', ')}\n`;
        }
      }
    } else if (componentType === 'HLD') {
      // For HLD: Include functional requirements, non-functional requirements, scale, APIs
      const functionalReqs = getCorrectItems(attemptData.functionalReqs || []);
      const nonFunctionalReqs = getCorrectItems(attemptData.nonFunctionalReqs || []);
      const scale = getCorrectItems(attemptData.scale || []);
      const apis = getCorrectItems(attemptData.apis || []);
      
      if (functionalReqs.length > 0 || nonFunctionalReqs.length > 0 || scale.length > 0 || apis.length > 0) {
        userContent = `\n\nUser's Already Added Content:\n`;
        
        if (functionalReqs.length > 0) {
          userContent += `- Functional Requirements: ${functionalReqs.join(', ')}\n`;
        }
        
        if (nonFunctionalReqs.length > 0) {
          userContent += `- Non-Functional Requirements: ${nonFunctionalReqs.join(', ')}\n`;
        }
        
        if (scale.length > 0) {
          userContent += `- Scale: ${scale.join(', ')}\n`;
        }
        
        if (apis.length > 0) {
          userContent += `- APIs: ${apis.join(', ')}\n`;
        }
      }
    }
    
    return {
      problemTitle,
      stepName: 'Clarifications',
      currentStepData: conversation,
      userClarifications: conversation.filter(msg => msg.type === 'user').map(msg => msg.content),
      conversationHistory: conversation,
      totalAttempts: conversation.length,
      userContent // Add the user's already added content
    };
  };

  // Get AI introduction for clarifications
  const getAIIntroduction = () => {
    const attemptKey = componentType === 'LLD' ? STORAGE_KEYS.LLD_ATTEMPT(problemId) : STORAGE_KEYS.HLD_ATTEMPT(problemId);
    const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Helper function to extract correct items (same as in getEnhancedContext)
    const getCorrectItems = (list) => {
      if (!list || !Array.isArray(list)) return [];
      
      return list
        .filter(item => {
          if (!item) return false;
          
          // If item has evaluationStatus, check if it's correct
          if (item.evaluationStatus !== undefined) {
            return item.evaluationStatus === 'correct' && 
                   item.userInput && 
                   item.userInput.trim();
          }
          
          // If item has text property (from TextListBuilder format)
          if (item.text !== undefined) {
            return item.text && item.text.trim();
          }
          
          // If item has userInput but no evaluationStatus, assume it's correct
          if (item.userInput !== undefined) {
            return item.userInput && item.userInput.trim();
          }
          
          // If item is a string, assume it's correct
          if (typeof item === 'string') {
            return item.trim();
          }
          
          return false;
        })
        .map(item => {
          // Extract the actual text content
          if (item.userInput) return item.userInput;
          if (item.text) return item.text;
          if (typeof item === 'string') return item;
          return '';
        })
        .filter(Boolean);
    };
    
    // Check if user has already added content
    let hasExistingContent = false;
    if (componentType === 'LLD') {
      const features = getCorrectItems(attemptData.features || []);
      const entities = getCorrectItems(attemptData.entities || []);
      const designPatterns = getCorrectItems(attemptData.designPatterns || []);
      hasExistingContent = features.length > 0 || entities.length > 0 || designPatterns.length > 0;
    } else if (componentType === 'HLD') {
      const functionalReqs = getCorrectItems(attemptData.functionalReqs || []);
      const nonFunctionalReqs = getCorrectItems(attemptData.nonFunctionalReqs || []);
      const scale = getCorrectItems(attemptData.scale || []);
      const apis = getCorrectItems(attemptData.apis || []);
      hasExistingContent = functionalReqs.length > 0 || nonFunctionalReqs.length > 0 || scale.length > 0 || apis.length > 0;
    }
    
    return AI_INTRODUCTIONS.CLARIFICATIONS.INITIAL(problemTitle, hasExistingContent);
  };

  // Send user message and get AI response
  const sendMessage = async () => {
    const text = userInput.trim();
    if (!text || isSendingMessage) return;

    setIsSendingMessage(true);

    // Add user message to conversation
    const userMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    const updatedConversation = [...conversation, userMessage];
    setConversation(updatedConversation);
    setUserInput('');
    setIsLoading(true);

    try {
      const context = getEnhancedContext();
      
      // Get problem understanding data for better context
      const problemUnderstandingData = getProblemUnderstandingData();
      
      // Get userId from localStorage
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';

      // Get proper schema based on component type (LLD or HLD)
      const clarificationSchema = getSchemaForComponent(componentType, 'CLARIFICATION');
      
      // Extract features for LLD, or functional requirements and scale for HLD
      const attemptKeyClarification = componentType === 'LLD' ? STORAGE_KEYS.LLD_ATTEMPT(problemId) : STORAGE_KEYS.HLD_ATTEMPT(problemId);
      const attemptDataClarification = JSON.parse(localStorage.getItem(attemptKeyClarification) || '{}');
      
      // Helper function to extract correct items
      const getCorrectItemsClarification = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list
          .filter(item => {
            if (!item) return false;
            if (item.evaluationStatus !== undefined) {
              return item.evaluationStatus === 'correct' && item.userInput && item.userInput.trim();
            }
            if (item.text !== undefined) return item.text && item.text.trim();
            if (item.userInput !== undefined) return item.userInput && item.userInput.trim();
            if (typeof item === 'string') return item.trim();
            return false;
          })
          .map(item => {
            if (item.userInput) return item.userInput;
            if (item.text) return item.text;
            if (typeof item === 'string') return item;
            return '';
          })
          .filter(Boolean);
      };
      
      // Get features for LLD, or functional requirements and scale for HLD
      let featuresForPrompt = [];
      let functionalReqsForPrompt = [];
      let scaleForPrompt = [];
      
      if (componentType === 'LLD') {
        featuresForPrompt = getCorrectItemsClarification(attemptDataClarification.features || []);
      } else {
        functionalReqsForPrompt = getCorrectItemsClarification(attemptDataClarification.functionalReqs || []);
        scaleForPrompt = getCorrectItemsClarification(attemptDataClarification.scale || []);
      }
      
      // Get AI response with enhanced context including problem understanding
      const aiResponse = await getAIFeedbackSync(
        (() => {
          const { HLD_CLARIFICATION_PROMPT, LLD_CLARIFICATION_PROMPT } = require('../../Utils/aiPrompts');
          const clarificationPrompt = componentType === 'HLD'
            ? HLD_CLARIFICATION_PROMPT(context.problemTitle, text, functionalReqsForPrompt, scaleForPrompt, context.conversationHistory)
            : LLD_CLARIFICATION_PROMPT(context.problemTitle, text, featuresForPrompt, context.conversationHistory);

          // Add problem understanding data to the prompt
          return clarificationPrompt.replace(
            'CRITICAL: Respond with ONLY a JSON object',
            `Problem Understanding Data (Use this to provide accurate answers):
${problemUnderstandingData}

CRITICAL: Respond with ONLY a JSON object`
          );
        })(),
        `Candidate is asking: ${text}. Answer ONLY if it is a scope, scale, or constraint question. If it is about design, architecture, or implementation, respond only with "That's your design decision to make."`,
        userId,
        'anthropic',
        null,
        clarificationSchema,
        {
          maxRetries: 3,
          onRetry: (attempt, error, delay) => {
          }
        }
      );

      if (!aiResponse) {
        throw new Error('Failed to get AI response');
      }

      // Parse AI response and extract only the answer field
      let displayContent = '';
      
      try {
        let responseData = aiResponse;
        
        // Check if response follows the API structure: {success: true, data: {response: "..."}}
        if (typeof aiResponse === 'object' && aiResponse.success && aiResponse.data && aiResponse.data.response) {
          responseData = aiResponse.data.response;
        } else if (typeof aiResponse === 'string') {
          // Try to parse as JSON first
          try {
            const jsonResponse = JSON.parse(aiResponse);
            if (jsonResponse.success && jsonResponse.data && jsonResponse.data.response) {
              responseData = jsonResponse.data.response;
            } else {
              responseData = aiResponse;
            }
          } catch (parseError) {
            // Fallback to original response
            responseData = aiResponse;
          }
        }
        
        // Extract only the answer field from the response
        if (typeof responseData === 'string') {
          try {
            const parsedContent = JSON.parse(responseData);
            if (parsedContent.answer) {
              displayContent = parsedContent.answer;
            } else {
              displayContent = responseData;
            }
          } catch (parseError) {
            // Not JSON, treat as plain text
            displayContent = responseData;
          }
        } else if (typeof responseData === 'object' && responseData !== null) {
          // Extract only the answer field
          if (responseData.answer) {
            displayContent = responseData.answer;
          } else {
            displayContent = JSON.stringify(responseData);
          }
        } else {
          displayContent = String(responseData);
        }
      } catch (error) {
        console.error('Error parsing AI response:', error);
        displayContent = typeof aiResponse === 'string' ? aiResponse : JSON.stringify(aiResponse);
      }

      // Validate display content
      if (!displayContent || typeof displayContent !== 'string' || displayContent.trim().length === 0) {
        throw new Error('AI response is empty or invalid');
      }

      // Add AI response to conversation
      const aiMessage = {
        id: `ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'ai',
        content: displayContent, // Use only the answer field
        timestamp: new Date().toISOString()
      };

      const finalConversation = [...updatedConversation, aiMessage];
      setConversation(finalConversation);

      // Save clarifications directly to main attempt storage
      const attemptKey = componentType === 'LLD' ? STORAGE_KEYS.LLD_ATTEMPT(problemId) : STORAGE_KEYS.HLD_ATTEMPT(problemId);
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      
      // Get existing clarifications
      const existingClarifications = attemptData.clarifications || [];
      
      // Add the new clarification
      const newClarification = {
        userInput: userMessage.content,
        aiResponse: displayContent, // Use only the answer field
        evaluationStatus: 'correct'
      };
      
      const updatedClarifications = [...existingClarifications, newClarification];
      
      attemptData.clarifications = updatedClarifications;
      attemptData.timestamp = Date.now();
      localStorage.setItem(attemptKey, JSON.stringify(attemptData));
      
      // Notify parent component with clarifications data
      // The parent component (HLDProblemDetails) will handle the metadata update via debouncedUpdate
      onClarificationsChange(updatedClarifications);
      
    } catch (error) {
      console.error('Error getting AI response:', error);
      setError('Failed to get AI response. Please try again.');
      setShowError(true);
      
      // Add error message to conversation
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'ai',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      
      const finalConversation = [...updatedConversation, errorMessage];
      setConversation(finalConversation);
    } finally {
      setIsLoading(false);
      setIsSendingMessage(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      sendMessage();
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden'
    }}>
      {/* Chat Area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        width: '100%',
        // Hide scrollbars but maintain scrolling functionality
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        '-ms-overflow-style': 'none',  // IE and Edge
        'scrollbarWidth': 'none',      // Firefox
      }}>
        <List sx={{ p: 0, width: '100%' }}>
          {conversation.map((message, index) => (
            <ListItem 
              key={message.id}
              sx={{ 
                p: 0, 
                mb: 1,
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                width: '100%'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                gap: 0.5,
                maxWidth: '100%',
                width: '100%',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '100%',
                  width: '100%'
                }}>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: message.type === 'user' ? '#008080' : '#f8f9fa',
                    color: message.type === 'user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    maxWidth: '100%',
                    width: 'fit-content',
                    minWidth: '80px',
                    border: message.type === 'ai' ? '1px solid #e0e0e0' : 'none',
                    boxShadow: message.type === 'ai' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}>
                    <Typography variant="body2" sx={{ 
                      wordBreak: 'break-word', 
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      display: 'block',
                      maxWidth: '100%'
                    }}>
                      {message.content}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </ListItem>
          ))}
          
          {isLoading && (
            <ListItem sx={{ p: 0, mb: 1, justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={20} />
              </Box>
            </ListItem>
          )}
          
          <div ref={chatEndRef} />
        </List>
      </Box>

      {/* Input Area - Simple and Fixed at Bottom */}
      <Box sx={{ 
        p:1,
        mt: 4,
        flexShrink: 0,
        position: 'sticky',
        bottom: 0
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center'
        }}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={5}
            placeholder={UI_TEXT.PLACEHOLDERS.CLARIFICATIONS}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSendingMessage}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#e0e0e0'
                },
                '&:hover fieldset': {
                  borderColor: '#bdbdbd'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#008080'
                }
              },
              '& .MuiInputBase-input': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap'
              }
            }}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={!userInput.trim() || isSendingMessage}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 1,
              height: 40,
              borderRadius: 2,
              backgroundColor: '#008080',
              '&:hover': { 
                backgroundColor: '#006666'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            <Send sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>



      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
